
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import FontBold from '../../../../element/icons/font-bold';
import FontFamilyPicker from './FontFamilyPicker';
import FontItalic from '../../../../element/icons/font-italic';


import { TOOLS_IDS } from '../../../../utils/constants';
import AnnotationOptions from '../../../../components/common/AnnotationOptions';
import FitTextToContentAction from '../../../../components/common/FitTextToContentAction';
import SidebarPanelButton from '../../../../components/common/SidebarPanelButton';
import restrictNumber from '../../../../utils/restrictNumber';
import {
  FONT_SIZE_MIN,
  fontSizeNeedsNormalization,
  normalizeFontSize,
} from '../../../../utils/normalizeFontSize';
import { useStore } from '../../../../hooks';
import { hasTemplateCardBackground } from '../../../../utils/textTemplateCardLayout';
import dispatchTextWithCardSync from '../../../../utils/dispatchTextWithCardSync';
import TextFontSizeControl from './TextFontSizeControl';
import { textOptionsPopupComponents, TEXT_POPPABLE_OPTIONS, TEXT_OPTION_ROW_GROUPS, FIT_TEXT_TO_CONTENT } from './TextOptions.constants';
import fitTextAnnotationToContent from '../../../../utils/fitTextAnnotationToContent';
import { patchTouchesTextTypography } from '../../../../utils/textTypographyAutoFit';
// textAutoHug: fit after every text property change when true. Typography (size, font, bold, spacing)
// always auto-fits so fixed template boxes grow when glyphs no longer fit.

const TextControls = ({ text, saveText, children = null }) => {
  const {
    annotations = {},
    dispatch,
    designLayer,
    selectionsIds = [],
    t,
    config,
    editorPreferences = {},
  } = useStore();
  const { useCloudimage } = config;
  const { fonts = [], onFontChange } = config[TOOLS_IDS.TEXT];
  const isLocked = Boolean(text?.locked);
  const textAutoHug = editorPreferences.textAutoHug === true;
  const textRef = useRef(text);
  textRef.current = text;
  const [fontSizeInput, setFontSizeInput] = useState(null);

  const saveTextWithOptionalHug = useCallback(
    (updater) => {
      const latest = textRef.current;
      const patch =
        typeof updater === 'function' ? updater(latest) : updater;
      const merged = {
        ...latest,
        ...(patch && typeof patch === 'object' ? patch : {}),
        id: latest.id,
      };

      let result = merged;
      if (textAutoHug || patchTouchesTextTypography(patch)) {
        result = fitTextAnnotationToContent(merged) || merged;
      }

      if (hasTemplateCardBackground(annotations, latest.id)) {
        dispatchTextWithCardSync(dispatch, annotations, result, designLayer);
      } else {
        saveText(result);
      }
    },
    [annotations, designLayer, dispatch, saveText, textAutoHug],
  );

  const changeTextProps = useCallback(
    (e) => {
      if (isLocked) {
        return;
      }
      const { name, value, type } = e.target;
      const nextValue =
        type === 'number'
          ? name === 'fontSize'
            ? normalizeFontSize(value)
            : restrictNumber(value, 1, 150)
          : value;
      if (name === 'fontSize') {
        setFontSizeInput(
          nextValue === '' ? '' : String(nextValue),
        );
      }
      saveTextWithOptionalHug((latestText) => ({
        id: latestText.id,
        [name]: nextValue,
      }));
    },
    [isLocked, saveTextWithOptionalHug],
  );

  const commitFontSizeIfNeeded = useCallback(() => {
    setFontSizeInput(null);
    if (isLocked || !fontSizeNeedsNormalization(text.fontSize)) {
      return;
    }
    saveTextWithOptionalHug({
      id: text.id,
      fontSize: normalizeFontSize(text.fontSize),
    });
  }, [isLocked, saveTextWithOptionalHug, text.fontSize, text.id]);

  useEffect(() => {
    if (fontSizeInput == null) {
      return;
    }
    const rounded = normalizeFontSize(text.fontSize);
    if (rounded !== '' && String(rounded) === String(fontSizeInput)) {
      setFontSizeInput(null);
    }
  }, [fontSizeInput, text.fontSize]);

  const resolveFontSizeNumber = useCallback(() => {
    if (fontSizeInput != null && fontSizeInput !== '') {
      const parsed = Number(fontSizeInput);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    const normalized = normalizeFontSize(text.fontSize);
    return normalized === '' ? FONT_SIZE_MIN : Number(normalized);
  }, [fontSizeInput, text.fontSize]);

  const stepFontSize = useCallback(
    (delta) => {
      if (isLocked) {
        return;
      }
      const next = normalizeFontSize(resolveFontSizeNumber() + delta);
      if (next === '') {
        return;
      }
      setFontSizeInput(String(next));
      saveTextWithOptionalHug({
        id: text.id,
        fontSize: next,
      });
    },
    [isLocked, resolveFontSizeNumber, saveTextWithOptionalHug, text.id],
  );

  const changeFontFamily = useCallback(
    (newFontFamily) => {
      changeTextProps({
        target: { name: 'fontFamily', value: newFontFamily },
      });
      if (
        text.fontFamily !== newFontFamily &&
        typeof onFontChange === 'function'
      ) {
        const reRenderCanvasFn = designLayer.draw.bind(designLayer);
        onFontChange(newFontFamily, reRenderCanvasFn);
      }
    },
    [changeTextProps, text, designLayer],
  );

  const changeFontStyle = useCallback(
    (newStyle) => {
      let fontStyle = text.fontStyle?.replace('normal', '').split(' ') || [];
      if (Object.keys(fontStyle).length > 0 && fontStyle.includes(newStyle)) {
        fontStyle = fontStyle.filter((style) => style !== newStyle);
      } else {
        fontStyle.push(newStyle);
      }

      changeTextProps({
        target: {
          name: 'fontStyle',
          value: fontStyle.join(' ').trim() || 'normal',
        },
      });
    },
    [text, changeTextProps],
  );

  const customOptionRowCells = useMemo(() => {
    if (isLocked || !text?.id) {
      return {};
    }
    return {
      [FIT_TEXT_TO_CONTENT]: (
        <FitTextToContentAction
          variant="sidebar"
          annotationIds={[text.id]}
          t={t}
        />
      ),
    };
  }, [isLocked, t, text?.id]);

  const fontSizeControl = useMemo(
    () => (
      <TextFontSizeControl
        disabled={isLocked}
        displayValue={
          fontSizeInput != null
            ? fontSizeInput
            : text.fontSize === '' || text.fontSize == null
              ? ''
              : normalizeFontSize(text.fontSize)
        }
        onFocus={() => {
          if (text.fontSize === '' || text.fontSize == null) {
            setFontSizeInput('');
            return;
          }
          setFontSizeInput(String(normalizeFontSize(text.fontSize)));
        }}
        onChange={changeTextProps}
        onBlur={commitFontSizeIfNeeded}
        onStep={stepFontSize}
        t={t}
      />
    ),
    [
      changeTextProps,
      commitFontSizeIfNeeded,
      fontSizeInput,
      isLocked,
      stepFontSize,
      t,
      text.fontSize,
    ],
  );

  const isSelectedTextOnCanvas = Boolean(
    text?.id &&
      selectionsIds.length === 1 &&
      selectionsIds[0] === text.id,
  );

  const fontFamilyControl = useMemo(
    () =>
      Array.isArray(fonts) && fonts.length > 1 ? (
        <FontFamilyPicker
          onChange={changeFontFamily}
          value={text.fontFamily}
          placeholder={t('fontFamily')}
          disabled={isLocked}
          isSelectionActive={isSelectedTextOnCanvas}
        />
      ) : null,
    [
      changeFontFamily,
      fonts,
      isLocked,
      isSelectedTextOnCanvas,
      t,
      text.fontFamily,
    ],
  );

  return (
    <AnnotationOptions
      className="respark_text-tool-options"
      annotation={text}
      updateAnnotation={saveTextWithOptionalHug}
      showTextStylesEntry
      preFillRowChildren={fontFamilyControl}
      fillRowCompanion={fontSizeControl}
      morePoppableOptionsPrepended={!useCloudimage ? TEXT_POPPABLE_OPTIONS : []}
      optionRowGroups={TEXT_OPTION_ROW_GROUPS}
      customOptionRowCells={customOptionRowCells}
      moreOptionsPopupComponentsObj={
        !useCloudimage ? textOptionsPopupComponents : {}
      }
      t={t}
    >
      {!useCloudimage && (
        <div className="respark_inspector-option-row respark_text-style-actions-row">
          <div className="respark_inspector-option-row-cell">
            <SidebarPanelButton
              className="respark_text-bold-option text-style-icon"
              Icon={FontBold}
              label={t('fontBold')}
              title={t('fontBold')}
              disabled={isLocked}
              aria-selected={(text.fontStyle || '').includes('bold')}
              onClick={() => changeFontStyle('bold')}
            />
          </div>
          <div className="respark_inspector-option-row-cell">
            <SidebarPanelButton
              className="respark_text-italic-option text-style-icon"
              Icon={FontItalic}
              label={t('fontItalic')}
              title={t('fontItalic')}
              disabled={isLocked}
              aria-selected={(text.fontStyle || '').includes('italic')}
              onClick={() => changeFontStyle('italic')}
            />
          </div>
        </div>
      )}
      {children}
    </AnnotationOptions>
  );
};


TextControls.propTypes = {
  text: PropTypes.instanceOf(Object).isRequired,
  saveText: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default TextControls;
